import { useMemo } from 'react';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

/**
 * @typedef {{
 *   isVisible: boolean,
 *   userAuthId: string | undefined
 * }} LostConnectionInfo
 */

const initialCenter = { lat: 39.8283, lng: -98.5795 };
const initialZoom = 3;

/**
 * @typedef {Object} MapUiStoreState
 * @property {boolean} showPermissionsModal
 * @property {string | null} selectedUser
 * @property {boolean} showLostConnectionListModal
 * @property {LostConnectionInfo} lostConnectionInfo
 * @property {{ lat: number, lng: number }} mapCenter
 * @property {number} mapZoom
 * @property {google.maps.Map | null} mapInstance
 */

/**
 * @typedef {Object} MapUiStoreActions
 * @property {(show: boolean) => void} setShowPermissionsModal
 * @property {(user: string | null) => void} setSelectedUser
 * @property {(show: boolean) => void} setShowLostConnectionListModal
 * @property {(info: LostConnectionInfo) => void} setLostConnectionInfo
 * @property {(center: { lat: number, lng: number }) => void} setMapCenter
 * @property {(zoom: number) => void} setMapZoom
 * @property {(instance: google.maps.Map | null) => void} setMapInstance
 */

/** @type {import('zustand').UseBoundStore<import('zustand').StoreApi<MapUiStoreState & MapUiStoreActions>>} */
export const useMapUiStore = create(
  subscribeWithSelector((set) => ({
    // State
    showPermissionsModal: false,
    selectedUser: null,
    showLostConnectionListModal: false,
    lostConnectionInfo: {
      isVisible: false,
      userAuthId: undefined,
    },
    mapCenter: initialCenter,
    mapZoom: initialZoom,
    mapInstance: null,

    // Actions
    setShowPermissionsModal: (show) => set({ showPermissionsModal: show }),
    setSelectedUser: (user) => set({ selectedUser: user }),
    setShowLostConnectionListModal: (show) =>
      set({ showLostConnectionListModal: show }),
    setLostConnectionInfo: (info) => set({ lostConnectionInfo: info }),
    setMapCenter: (center) => set({ mapCenter: center }),
    setMapZoom: (zoom) => set({ mapZoom: zoom }),
    setMapInstance: (instance) => set({ mapInstance: instance }),
  })),
);

// Selector hooks for efficient re-renders
export const useShowPermissionsModal = () =>
  useMapUiStore((state) => state.showPermissionsModal);
export const useSelectedUser = () =>
  useMapUiStore((state) => state.selectedUser);
export const useShowLostConnectionListModal = () =>
  useMapUiStore((state) => state.showLostConnectionListModal);
export const useLostConnectionInfo = () =>
  useMapUiStore((state) => state.lostConnectionInfo);
export const useMapCenter = () => useMapUiStore((state) => state.mapCenter);
export const useMapZoom = () => useMapUiStore((state) => state.mapZoom);
export const useMapHeight = () => useMapUiStore((state) => state.mapHeight);
export const useMapInstance = () => useMapUiStore((state) => state.mapInstance);

// Action hooks. The individual Zustand actions are stable, but the wrapping
// object literal is not — memoize it so consumers can safely put the whole
// object in a useCallback/useEffect dependency array without retriggering
// on every render (see Map.jsx's initializeMap/mount-effect chain).
export const useMapUiActions = () => {
  const store = useMapUiStore.getState();
  return useMemo(
    () => ({
      setShowPermissionsModal: store.setShowPermissionsModal,
      setSelectedUser: store.setSelectedUser,
      setShowLostConnectionListModal: store.setShowLostConnectionListModal,
      setLostConnectionInfo: store.setLostConnectionInfo,
      setMapCenter: store.setMapCenter,
      setMapZoom: store.setMapZoom,
      setMapInstance: store.setMapInstance,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- these are stable bound functions from Zustand's create(); they never change identity.
    [],
  );
};
